/**
 * A set of sibling products differing along one attribute. Siblings are
 * SEPARATE products with their own SKU and slug, not variations of one
 * product, so selecting one navigates to its PDP.
 *
 * Served by the Facade on `GET /page/productDetail` as `productGroup`.
 *
 * Contract notes callers must respect:
 * - No `isCurrent` flag; match the viewed product on `sku`.
 * - `order` is server-normalised to the array index — a stable fallback, not a
 *   mandate. The PDP selector sorts each axis cheapest-first instead.
 * - Unpublished members are dropped server-side, so a non-null group with an
 *   empty `members` array is reachable.
 * - Members carry no image, price, or stock; those need a lookup by SKU.
 */
export interface ProductGroupView {
  groupName: string;
  /** Free-form attribute name. Only 'color' and 'size' exist today. */
  memberAttribute: string;
  members: ProductGroupViewMember[];
}

export type ProductGroupViewMember = ProductGroupViewProductMember | ProductGroupViewBranchMember;

/** Leaf — a purchasable sibling product. */
export interface ProductGroupViewProductMember {
  value: string;
  sku: string;
  slug: string;
  name: string;
  order?: number;
}

/** Branch — a nested axis (e.g. color → size). */
export interface ProductGroupViewBranchMember {
  value: string;
  memberAttribute: string;
  members: ProductGroupViewMember[];
  order?: number;
}

export const isProductGroupProductMember = (member: ProductGroupViewMember): member is ProductGroupViewProductMember =>
  'sku' in member;
